import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  ChevronDown,
  Download,
  Inbox,
  RefreshCw,
  Search,
  Settings2,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { StatTile } from '@/components/ui/stat-tile';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useProfile } from '@/features/auth/auth.store';
import type { Business } from '@/features/auth/types';
import { OrderActionsSheet } from '@/features/orders/order-actions-sheet';
import { SlotRange, StatusBadge } from '@/features/orders/order-cells';
import { OrderDetail } from '@/features/orders/order-detail';
import { formatCurrency, formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  currentWeek,
  DEFAULT_STATUS,
  DELIVERY_STATUSES,
  useDeliveryHeader,
  useDeliveryProviders,
  useDeliverySearch,
  useDeliveryZones,
  useExportDeliveries,
  type DeliveryFilters,
  type DeliveryOrder,
} from './deliveries.api';

/**
 * The store's deliveries: where each one is, who is driving it, and the order
 * behind it.
 *
 * The original ran the search from two overlapping effects plus the submit
 * handler, so one filter change could fire it twice and the slower answer won.
 * It also listed the items in a popover per row; that is the Items tab of the
 * order actions sheet, which this page opens the same way the global order
 * search does.
 */

/** Radix's Select cannot hold an empty value, so "any" travels as a sentinel. */
const ANY = 'ANY';
const toValue = (id: string) => id || ANY;
const fromValue = (value: string) => (value === ANY ? '' : value);

export function DeliveriesSearch({ business }: { business: Business }) {
  const { t } = useTranslation();
  const profile = useProfile();
  const locationId = business.locations?.[0]?.id ?? null;

  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const [range, setRange] = useState(currentWeek);
  /** `null` until somebody chooses: the first provider is then the default. */
  const [providerChoice, setProviderChoice] = useState<string | null>(null);
  const [zoneId, setZoneId] = useState('');
  const [status, setStatus] = useState<string>(DEFAULT_STATUS);
  const [sort, setSort] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [actionsFor, setActionsFor] = useState<DeliveryOrder | null>(null);

  const providers = useDeliveryProviders(profile?.id ?? null);
  // The search waits for the providers, because the first of them is its
  // default — asking before then would fetch "any provider" and throw it away.
  const providersReady = !providers.isLoading;
  const providerId = providerChoice ?? providers.data?.[0]?.id ?? '';
  const zones = useDeliveryZones(locationId, providerId, providersReady);

  const filters: DeliveryFilters = { ...range, providerId, zoneId, status };

  const search = useDeliverySearch({
    businessId: business.id,
    filters,
    term,
    page,
    size,
    sort,
    enabled: providersReady,
  });
  const header = useDeliveryHeader(business.id, filters, providersReady);
  const exportAll = useExportDeliveries();

  const orders = search.data?.orders ?? [];
  const total = search.data?.count ?? 0;
  // `isLoading`, not `isPending`: the search waits on the providers.
  const loading = search.isLoading || !providersReady;
  const busy = search.isFetching && !loading;
  const refreshing = search.isFetching || header.isFetching;

  // Any change to what is being asked for resets to the first page.
  const fields: FilterField[] = [
    {
      id: 'start',
      label: t('deliveries.from'),
      type: 'date',
      value: range.start,
      onChange: (start) => {
        setRange((current) => ({ ...current, start }));
        setPage(0);
      },
    },
    {
      id: 'end',
      label: t('deliveries.to'),
      type: 'date',
      value: range.end,
      onChange: (end) => {
        setRange((current) => ({ ...current, end }));
        setPage(0);
      },
    },
    // An account that works for no provider has nothing to choose between.
    ...((providers.data?.length ?? 0) > 0
      ? [
          {
            id: 'provider',
            label: t('deliveries.provider'),
            value: toValue(providerId),
            // The default is the account's first provider, not "any".
            resetValue: toValue(providers.data?.[0]?.id ?? ''),
            onChange: (value: string) => {
              setProviderChoice(fromValue(value));
              // Zones belong to a provider; the old choice may not exist there.
              setZoneId('');
              setPage(0);
            },
            options: [
              { value: ANY, label: t('deliveries.anyProvider') },
              ...(providers.data ?? []).map((provider) => ({
                value: provider.id,
                label: provider.name ?? '',
              })),
            ],
          } satisfies FilterField,
        ]
      : []),
    {
      id: 'zone',
      label: t('deliveries.zone'),
      value: toValue(zoneId),
      disabled: zones.isLoading,
      onChange: (value) => {
        setZoneId(fromValue(value));
        setPage(0);
      },
      options: [
        { value: ANY, label: t('deliveries.anyZone') },
        ...(zones.data ?? []).map((zone) => ({
          value: zone.id,
          label: zone.name ?? '',
        })),
      ],
    },
    {
      id: 'status',
      label: t('deliveries.statusFilter'),
      value: status,
      resetValue: DEFAULT_STATUS,
      // All, then the delivery's own lifecycle.
      sort: false,
      onChange: (value) => {
        setStatus(value);
        setPage(0);
      },
      options: DELIVERY_STATUSES.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    },
    {
      id: 'sort',
      label: t('orders.sort'),
      value: sort,
      resetValue: 'asc',
      sort: false,
      onChange: (value) => {
        setSort(value as 'asc' | 'desc');
        setPage(0);
      },
      options: [
        { value: 'asc', label: t('orders.oldest') },
        { value: 'desc', label: t('orders.newest') },
      ],
    },
  ];

  const tiles = [
    { key: 'pending', label: t('orders.stats.pending'), value: header.data?.pendingOrders },
    { key: 'active', label: t('orders.stats.processing'), value: header.data?.activeOrders },
    { key: 'waiting', label: t('orders.stats.waiting'), value: header.data?.waitingOrders },
    { key: 'inTransit', label: t('orders.stats.inTransit'), value: header.data?.inTransitOrders },
    { key: 'completed', label: t('orders.stats.completed'), value: header.data?.completedOrders },
  ];

  return (
    <div className="space-y-6">
      {/* The pipeline for the dates, provider and zone chosen below — every
          status, whatever the status filter is narrowing the list to. */}
      {!header.isError && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
          {tiles.map((tile) => (
            <StatTile
              key={tile.key}
              label={tile.label}
              value={formatNumber(tile.value ?? 0)}
              loading={header.isLoading || !providersReady}
              busy={header.isFetching}
            />
          ))}
        </div>
      )}

      <FilterBar
        busy={search.isFetching}
        fields={fields}
        columns={6}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              // The same term does not change the query key, so without this
              // the button would do nothing.
              if (draft === term && page === 0) void search.refetch();
              setTerm(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t('orders.searchPlaceholder')}
                aria-label={t('orders.searchLabel')}
                className="h-10 pr-9 pl-9"
              />
              {draft && (
                <button
                  type="button"
                  onClick={() => {
                    setDraft('');
                    setTerm('');
                    setPage(0);
                  }}
                  aria-label={t('customers.clearSearch')}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 flex size-6 -translate-y-1/2 items-center justify-center rounded"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className={cn(
            'text-muted-foreground text-sm',
            busy && 'opacity-60 transition-opacity'
          )}
        >
          {search.isSuccess && t('deliveries.resultCount', { count: total })}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={refreshing}
            onClick={() => {
              void search.refetch();
              void header.refetch();
            }}
          >
            <RefreshCw className={cn(refreshing && 'animate-spin')} />
            <span className="hidden sm:inline">{t('common.reload')}</span>
          </Button>
          {/* Everything the filters match, not the page on screen. */}
          <Button
            variant="outline"
            size="sm"
            disabled={exportAll.isPending || loading || total === 0}
            onClick={() =>
              exportAll.mutate(
                { businessId: business.id, filters, term, sort },
                {
                  onError: (error) =>
                    toast.error(
                      error instanceof Error && error.message
                        ? error.message
                        : t('deliveries.exportFailed')
                    ),
                }
              )
            }
          >
            <Download className={cn(exportAll.isPending && 'animate-pulse')} />
            {exportAll.isPending
              ? t('deliveries.exporting')
              : t('deliveries.export')}
          </Button>
        </div>
      </div>

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>
              {search.error instanceof Error && search.error.message
                ? search.error.message
                : t('deliveries.loadFailed')}
            </p>
            <Button variant="outline" size="sm" onClick={() => search.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {loading && <ListSkeleton />}

      {search.isSuccess && orders.length === 0 && (
        <div className="relative flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-14 text-center">
          <BusyOverlay show={busy} />
          <span className="bg-muted text-muted-foreground flex size-14 items-center justify-center rounded-full">
            <Inbox className="size-7" />
          </span>
          <div className="space-y-1">
            <p className="font-medium">{t('deliveries.noResultsTitle')}</p>
            <p className="text-muted-foreground max-w-sm text-sm">
              {t('deliveries.noResultsBody')}
            </p>
          </div>
        </div>
      )}

      {search.isSuccess && orders.length > 0 && (
        <div className="space-y-3">
          {/* Desktop */}
          <div className="hidden rounded-xl border lg:block">
            <Table busy={busy}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8" />
                  <TableHead>{t('orders.zone')}</TableHead>
                  <TableHead>{t('orders.customer')}</TableHead>
                  <TableHead>{t('orders.driver')}</TableHead>
                  <TableHead className="text-right">
                    {t('orders.total')}
                  </TableHead>
                  <TableHead>{t('orders.statusColumn')}</TableHead>
                  <TableHead>{t('orders.serviceWindow')}</TableHead>
                  <TableHead>{t('deliveries.delivered')}</TableHead>
                  <TableHead className="text-right">
                    {t('orders.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => {
                  const open = expanded === order.id;
                  return (
                    <Fragment key={order.id}>
                      <TableRow
                        onClick={() => setExpanded(open ? null : order.id)}
                        aria-expanded={open}
                        className={cn(
                          'cursor-pointer',
                          open && 'bg-muted/40 border-b-0'
                        )}
                      >
                        <TableCell>
                          <ChevronDown
                            className={cn(
                              'text-muted-foreground size-4 transition-transform',
                              open && 'rotate-180'
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">
                            {order.deliveryZoneName || '—'}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {order.customerLocation?.city}
                          </p>
                        </TableCell>
                        <TableCell className="max-w-[14rem]">
                          <p className="truncate">
                            {order.customerName || '—'}
                          </p>
                          <p className="text-muted-foreground font-mono text-xs">
                            {order.confirmationCode}
                          </p>
                        </TableCell>
                        <TableCell>
                          <DriverBadge name={order.driverName} />
                        </TableCell>
                        <TableCell className="text-right">
                          <p className="font-medium tabular-nums">
                            {formatCurrency(order.orderTotal)}
                          </p>
                          <p className="text-muted-foreground text-xs tabular-nums">
                            <Contents order={order} />
                          </p>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={order.status} />
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <p>{formatDate(order.desiredDateTime)}</p>
                          <p className="text-muted-foreground text-xs">
                            <SlotRange order={order} />
                          </p>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(order.dispatchDateTime)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setActionsFor(order);
                            }}
                          >
                            <Settings2 className="size-3.5" />
                            {t('orders.actions')}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {open && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={9} className="bg-muted/40 p-4">
                            <OrderDetail order={order} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Phone and tablet. */}
          <div className="relative lg:hidden">
            <BusyOverlay show={busy} />
            <div
              aria-busy={busy}
              className={cn('space-y-2', busy && 'opacity-60 transition-opacity')}
            >
              {orders.map((order) => {
                const open = expanded === order.id;
                return (
                  <div
                    key={order.id}
                    className="bg-card overflow-hidden rounded-xl border"
                  >
                    <button
                      type="button"
                      onClick={() => setExpanded(open ? null : order.id)}
                      aria-expanded={open}
                      className="w-full space-y-2 p-3 text-left"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {order.customerName || '—'}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">
                            {[order.deliveryZoneName, order.customerLocation?.city]
                              .filter(Boolean)
                              .join(' · ')}
                            {order.confirmationCode && (
                              <span className="font-mono">
                                {' '}
                                · {order.confirmationCode}
                              </span>
                            )}
                          </p>
                        </div>
                        <ChevronDown
                          className={cn(
                            'text-muted-foreground mt-1 size-4 shrink-0 transition-transform',
                            open && 'rotate-180'
                          )}
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <StatusBadge status={order.status} />
                        <DriverBadge name={order.driverName} />
                        <span className="text-sm font-semibold tabular-nums">
                          {formatCurrency(order.orderTotal)}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {formatDate(order.desiredDateTime)},{' '}
                          <SlotRange order={order} />
                        </span>
                      </div>
                    </button>
                    {open && (
                      <div className="bg-muted/40 space-y-3 border-t p-3">
                        <OrderDetail order={order} />
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => setActionsFor(order)}
                        >
                          <Settings2 className="size-3.5" />
                          {t('orders.actions')}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      <OrderActionsSheet
        order={actionsFor}
        open={actionsFor !== null}
        onOpenChange={(next) => {
          if (!next) setActionsFor(null);
        }}
      />
    </div>
  );
}

// ------------------------------------------------------------------ helpers

/** No driver yet is the thing a dispatcher scans this column for. */
export function DriverBadge({ name }: { name: string | null }) {
  const { t } = useTranslation();
  return name ? (
    <Badge variant="success">{name}</Badge>
  ) : (
    <Badge variant="outline">{t('deliveries.unassigned')}</Badge>
  );
}

/** "3 bags · 12 items" — what the driver is collecting. */
export function Contents({ order }: { order: DeliveryOrder }) {
  const { t } = useTranslation();
  return [
    order.bags != null ? t('deliveries.bagCount', { count: order.bags }) : '',
    t('orders.itemCount', { count: order.itemCount ?? 0 }),
  ]
    .filter(Boolean)
    .join(' · ');
}

function ListSkeleton() {
  return (
    <div className="divide-y rounded-xl border">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 p-3">
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-5 w-20" />
          <Skeleton className="hidden h-5 w-16 sm:block" />
        </div>
      ))}
    </div>
  );
}
